/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useRef, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { KeyboardAvoidingView, type KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { webOperationalScrollBindings } from '../../shared/theme/webScrollbar';
import { ticketColors } from '../../shared/theme/ticketColors';
import { DEFAULT_PAYMENT_METHOD_LABELS } from '../../sales/public-api';
import { settingsStyles as styles } from '../../settings/screens/settingsScreen.styles';
import type { useCashManagement } from './useCashManagement';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import {
  formatCashArqueoResult,
  formatMoney,
} from './workspace/cashManagement.utils';
import { cashArqueoCopy } from './workspace/cashArqueoCopy';
import {
  buildCloseTurnoHeroSubtitle,
  buildCloseTurnoHeroTitle,
  formatTodayContextNote,
  formatTurnoOpenedAtShort,
  getNonCashSessionPaymentLines,
  isTurnoOpenedOnPriorCalendarDay,
} from './workspace/cashCloseTurnoPresentation';
import { cashStyles } from './workspace/cashManagement.styles';
import { captureCatch } from '../../shared/sentry/sentryUser';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';

type CashVm = ReturnType<typeof useCashManagement>;

function parseAmount(raw: string): number | null {
  if (raw.trim() === '') return null;
  return parseDecimalInput(raw, { min: 0, money: true });
}

type Props = {
  visible: boolean;
  vm: CashVm;
  /** Totales del turno refrescándose al abrir el modal. */
  totalsLoading?: boolean;
  onClose: () => void;
};

export function CashCloseTurnoModal({ visible, vm, totalsLoading = false, onClose }: Props) {
  const backdropPadding = useModalBackdropPadding();
  const { height: winH } = useWindowDimensions();
  const { layoutWebShell } = useContentLayout('dataWide');
  const mobile = !layoutWebShell;
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const scrollContentRef = useRef<View>(null);
  const countedInputRef = useRef<TextInput>(null);
  const noteInputRef = useRef<TextInput>(null);
  const countedPreview = parseAmount(vm.closingAmount);
  const expected = vm.expectedCashInDrawer;
  const differencePreview = useMemo(() => {
    if (expected == null || countedPreview == null) return null;
    return countedPreview - expected;
  }, [expected, countedPreview]);

  const activeSession = vm.cashSummary?.activeSession;
  const openingAmount = activeSession?.openingAmount ?? 0;
  const sessionCashTotal = vm.cashSummary?.sessionCashTotal ?? 0;
  // sessionCashTotal incluye ingresos manuales (direction='in'); se muestran aparte.
  const sessionInflowsTotal = vm.cashSummary?.sessionInflowsTotal ?? 0;
  const sessionCashSalesTotal = sessionCashTotal - sessionInflowsTotal;
  const sessionOutflowsTotal = vm.cashSummary?.sessionOutflowsTotal ?? 0;
  const sessionSalesCount = vm.cashSummary?.sessionSalesCount ?? 0;
  const sessionSalesTotal = vm.cashSummary?.sessionSalesTotal ?? 0;
  const sessionPaymentBreakdown = vm.cashSummary?.sessionPaymentBreakdown ?? [];
  const todaySalesCount = vm.cashSummary?.todaySalesCount ?? 0;
  const todaySalesTotal = vm.cashSummary?.todaySalesTotal ?? 0;
  const openedAt = activeSession?.openedAt ?? '';
  const multiDayTurn = openedAt ? isTurnoOpenedOnPriorCalendarDay(openedAt) : false;
  const nonCashLines = getNonCashSessionPaymentLines(sessionPaymentBreakdown);
  const todayContextNote = multiDayTurn ? formatTodayContextNote(todaySalesCount, todaySalesTotal) : null;
  const openingRowLabel = multiDayTurn
    ? `${cashArqueoCopy.closeTurnoOpeningRowLabel} ${formatTurnoOpenedAtShort(openedAt)}`
    : cashArqueoCopy.closeTurnoOpeningRowLabel;

  const showDifference = differencePreview != null && expected != null;
  const expectedNegative = expected != null && expected < 0;
  const diffExact = showDifference && differencePreview === 0;
  const diffSurplus = showDifference && differencePreview > 0 && !expectedNegative;
  const diffSurplusVsNegativeExpected =
    showDifference && differencePreview > 0 && expectedNegative;
  const diffShortage = showDifference && differencePreview < 0;
  const requiresDifferenceNote = showDifference && differencePreview !== 0;
  const noteMissing = requiresDifferenceNote && vm.closingNote.trim() === '';
  const canConfirmClose =
    !!activeSession && vm.canEdit && !vm.closingCash && !noteMissing && !totalsLoading;

  const { confirm: confirmDiscard, ConfirmModal: DiscardConfirmModal } = useOperationalConfirm();
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const isDirty = vm.closingAmount.trim() !== '' || vm.closingNote.trim() !== '';

  // BUG-02: no permitir cerrar el modal mientras el cierre está en progreso.
  // BUG-06: con contado/nota cargados, confirmar antes de descartarlos.
  const safeOnClose = () => {
    if (vm.closingCash) return;
    if (discardConfirmOpen) return;
    if (!isDirty) {
      onClose();
      return;
    }
    setDiscardConfirmOpen(true);
    void confirmDiscard({
      title: 'Descartar arqueo',
      message: 'Ya cargaste datos del conteo. Si cerrás, se pierden.',
      confirmLabel: 'Descartar',
      destructive: true,
      iconName: 'trash-outline',
      presentation: 'overlay',
    }).then((confirmed) => {
      setDiscardConfirmOpen(false);
      if (confirmed) onClose();
    });
  };

  const submitClose = async () => {
    try {
      const closed = await vm.handleCloseCash();
      if (closed) {
        onClose();
      }
    } catch (error) {
      // cancelación o error: mantener modal abierto
      captureCatch('cash', 'close-turno-confirm')(error);
    }
  };
  const submitOnEnter = () => {
    if (canConfirmClose) {
      void submitClose();
    }
  };

  if (!activeSession) {
    return (
      <Modal
        visible={visible}
        transparent
        animationType={mobile ? 'slide' : 'fade'}
        onRequestClose={safeOnClose}
        accessibilityViewIsModal
      >
        <View
          style={[
            posUi.modalBackdrop,
            mobile ? { justifyContent: 'flex-end', padding: 0 } : modalExperience.modalRoot,
            mobile ? null : backdropPadding,
          ]}
        >
          <View
            style={[
              mobile
                ? [cashStyles.sheetMobileContainer, { maxHeight: winH * 0.4, width: '100%', zIndex: 1 }]
                : [
                    posUi.modalCard,
                    modalShellShared.modalCardShell,
                    modalExperience.modalWidthStandard,
                    localStyles.card,
                  ],
            ]}
          >
            {mobile ? (
              <>
                <View style={cashStyles.sheetMobileGrabber} />
                <View style={cashStyles.sheetMobileHeader}>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={cashStyles.sheetMobileTitle}>{cashArqueoCopy.closeTurnoModalTitle}</Text>
                  </View>
                  <TouchableOpacity
                    style={cashStyles.sheetMobileClose}
                    onPress={safeOnClose}
                    accessibilityRole="button"
                    accessibilityLabel="Cerrar"
                  >
                    <Text style={cashStyles.sheetMobileCloseText}>✕</Text>
                  </TouchableOpacity>
                </View>
              </>
            ) : (
              <ModalScreenHeader title={cashArqueoCopy.closeTurnoModalTitle} onClose={safeOnClose} />
            )}
            <View style={localStyles.loadingBody}>
              <ActivityIndicator color={ticketColors.gold} />
              <Text style={localStyles.loadingText}>Preparando arqueo…</Text>
            </View>
          </View>
          {DiscardConfirmModal}
        </View>
      </Modal>
    );
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType={mobile ? 'slide' : 'fade'}
      onRequestClose={safeOnClose}
      accessibilityViewIsModal
    >
      <View
        style={[
          posUi.modalBackdrop,
          mobile ? { justifyContent: 'flex-end', padding: 0 } : modalExperience.modalRoot,
          mobile ? null : backdropPadding,
        ]}
      >
        {mobile ? (
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={safeOnClose}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
        ) : null}
        <KeyboardAvoidingViewIfMobile enabled={mobile}>
        <View
          style={[
            mobile
              ? [cashStyles.sheetMobileContainer, { maxHeight: winH * 0.96, width: '100%', zIndex: 1 }]
              : [
                  posUi.modalCard,
                  modalShellShared.modalCardShell,
                  modalExperience.modalWidthStandard,
                  localStyles.card,
                ],
          ]}
        >
          {mobile ? (
            <>
              <View style={cashStyles.sheetMobileGrabber} />
              <View style={cashStyles.sheetMobileHeader}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={cashStyles.sheetMobileTitle}>{cashArqueoCopy.closeTurnoModalTitle}</Text>
                </View>
                <TouchableOpacity
                  style={cashStyles.sheetMobileClose}
                  onPress={safeOnClose}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Text style={cashStyles.sheetMobileCloseText}>✕</Text>
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <ModalScreenHeader title={cashArqueoCopy.closeTurnoModalTitle} onClose={safeOnClose} />
          )}
          <ModalAwareScrollView
            ref={scrollRef}
            {...webOperationalScrollBindings({ flexShrink: 1, minHeight: 0 })}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
            bottomOffset={spacing.sm}
            contentContainerStyle={
              mobile
                ? { paddingHorizontal: spacing.md, paddingBottom: spacing.sm }
                : { paddingBottom: spacing.sm }
            }
          >
            <View
              ref={scrollContentRef}
              collapsable={false}
              style={[localStyles.body, mobile ? null : modalExperience.formPanel]}
            >
              {totalsLoading ? (
                <View style={localStyles.loadingInline}>
                  <ActivityIndicator size="small" color={ticketColors.gold} />
                  <Text style={localStyles.loadingText}>Actualizando totales del turno…</Text>
                </View>
              ) : null}

              {openedAt ? (
                <View style={localStyles.heroBlock}>
                  <Text style={localStyles.heroTitle}>{buildCloseTurnoHeroTitle(openedAt)}</Text>
                  <Text style={localStyles.heroSubtitle}>
                    {buildCloseTurnoHeroSubtitle(sessionSalesCount, sessionSalesTotal, multiDayTurn)}
                  </Text>
                </View>
              ) : null}

              {expected != null ? (
                <>
                  <Text style={localStyles.sectionTitle}>{cashArqueoCopy.closeTurnoCashSectionTitle}</Text>
                  <View style={localStyles.breakdownBlock}>
                    <View style={localStyles.payRow}>
                      <Text style={localStyles.payRowLabel}>{openingRowLabel}</Text>
                      <Text style={localStyles.payRowValue}>{formatMoney(openingAmount)}</Text>
                    </View>
                    {sessionCashSalesTotal > 0 ? (
                      <View style={localStyles.payRow}>
                        <Text style={localStyles.payRowLabel}>{cashArqueoCopy.closeTurnoCashSalesRowLabel}</Text>
                        <Text style={localStyles.payRowValue}>{formatMoney(sessionCashSalesTotal)}</Text>
                      </View>
                    ) : null}
                    {vm.cashOutflowsEnabled && sessionInflowsTotal > 0 ? (
                      <View style={localStyles.payRow}>
                        <Text style={localStyles.payRowLabel}>{cashArqueoCopy.closeTurnoInflowsRowLabel}</Text>
                        <Text style={localStyles.payRowValue}>{formatMoney(sessionInflowsTotal)}</Text>
                      </View>
                    ) : null}
                    {vm.cashOutflowsEnabled && sessionOutflowsTotal > 0 ? (
                      <View style={localStyles.payRow}>
                        <Text style={localStyles.payRowLabel}>{cashArqueoCopy.closeTurnoOutflowsRowLabel}</Text>
                        <Text style={localStyles.payRowValue}>{formatMoney(sessionOutflowsTotal)}</Text>
                      </View>
                    ) : null}
                  </View>

                  {nonCashLines.length > 0 ? (
                    <>
                      <Text style={localStyles.sectionTitle}>{cashArqueoCopy.closeTurnoNonCashSectionTitle}</Text>
                      <View style={localStyles.breakdownBlock}>
                        {nonCashLines.map((line) => (
                          <View key={line.methodId} style={localStyles.payRow}>
                            <Text style={localStyles.payRowLabel}>
                              {DEFAULT_PAYMENT_METHOD_LABELS[line.methodId] ?? line.methodId}
                            </Text>
                            <Text style={localStyles.payRowValue}>{formatMoney(line.total)}</Text>
                          </View>
                        ))}
                      </View>
                    </>
                  ) : null}

                  {todayContextNote ? (
                    <Text style={localStyles.todayContextNote}>{todayContextNote}</Text>
                  ) : null}
                </>
              ) : null}

              {expected != null ? (
                <View style={localStyles.arqueoBlock}>
                  <View style={localStyles.arqueoGrid}>
                    <View style={localStyles.arqueoColumn}>
                      <Text style={localStyles.arqueoLabel}>{cashArqueoCopy.theoreticalBalance}</Text>
                      <Text
                        style={[
                          localStyles.arqueoAmount,
                          expectedNegative && localStyles.arqueoAmountNegative,
                        ]}
                        numberOfLines={1}
                        adjustsFontSizeToFit
                        minimumFontScale={0.62}
                      >
                        {formatMoney(expected)}
                      </Text>
                      {expectedNegative ? (
                        <Text style={localStyles.arqueoFieldHint}>
                          {cashArqueoCopy.theoreticalHintNegative}
                        </Text>
                      ) : null}
                    </View>

                    <View style={[localStyles.arqueoColumn, localStyles.arqueoColumnCounted]}>
                      <Text style={localStyles.arqueoLabel}>{cashArqueoCopy.countedCash}</Text>
                      <NumericTextInput
                        ref={countedInputRef}
                        kind="decimalMoney"
                        value={vm.closingAmount}
                        onChangeText={vm.setClosingAmount}
                        placeholder="0"
                        placeholderTextColor={ticketColors.ink3}
                        style={localStyles.arqueoInput}
                        editable={vm.canEdit && !totalsLoading}
                        autoFocus
                        returnKeyType="done"
                        onSubmitEditing={submitOnEnter}
                        accessibilityLabel={cashArqueoCopy.countedInputA11y}
                      />
                    </View>

                    <View style={localStyles.arqueoColumn}>
                      <Text style={localStyles.arqueoLabel}>{cashArqueoCopy.difference}</Text>
                      {showDifference ? (
                        <Text
                          style={[
                            localStyles.arqueoAmount,
                            diffExact && localStyles.arqueoDiffExact,
                            diffSurplus && localStyles.arqueoDiffSurplus,
                            diffSurplusVsNegativeExpected && localStyles.arqueoDiffWarn,
                            diffShortage && localStyles.arqueoDiffShortage,
                          ]}
                          numberOfLines={1}
                          adjustsFontSizeToFit
                          minimumFontScale={0.62}
                        >
                          {differencePreview === 0
                            ? cashArqueoCopy.noDifference
                            : differencePreview > 0
                              ? `+${formatMoney(differencePreview)}`
                              : formatMoney(differencePreview)}
                        </Text>
                      ) : (
                        <Text style={localStyles.arqueoDiffPlaceholder}>—</Text>
                      )}
                      {showDifference && differencePreview !== 0 ? (
                        <Text style={localStyles.arqueoFieldHint}>
                          {formatCashArqueoResult(differencePreview, { expectedNegative })}
                        </Text>
                      ) : null}
                    </View>
                  </View>
                </View>
              ) : (
                <ModalFormField label={cashArqueoCopy.countedFieldLabel} required>
                  <NumericTextInput
                    ref={countedInputRef}
                    kind="decimalMoney"
                    value={vm.closingAmount}
                    onChangeText={vm.setClosingAmount}
                    placeholder="0"
                    placeholderTextColor={ticketColors.ink3}
                    style={modalExperience.formInput}
                    editable={vm.canEdit}
                    autoFocus
                    returnKeyType="done"
                    onSubmitEditing={submitOnEnter}
                    accessibilityLabel={cashArqueoCopy.countedInputA11y}
                  />
                </ModalFormField>
              )}

              <ModalFormField
                label={
                  requiresDifferenceNote
                    ? cashArqueoCopy.differenceNoteLabel
                    : cashArqueoCopy.notesOptionalLabel
                }
                required={requiresDifferenceNote}
              >
                <TextInput
                  ref={noteInputRef}
                  value={vm.closingNote}
                  onChangeText={vm.setClosingNote}
                  placeholder={
                    requiresDifferenceNote
                      ? cashArqueoCopy.differenceNotePlaceholder
                      : cashArqueoCopy.notesOptionalPlaceholder
                  }
                  placeholderTextColor={ticketColors.ink3}
                  style={[modalExperience.formInput, modalExperience.formInputMultiline]}
                  multiline
                  editable={vm.canEdit}
                />
              </ModalFormField>
            </View>
          </ModalAwareScrollView>
          <View style={mobile ? cashStyles.sheetMobileFooter : localStyles.footerActions}>
            <TouchableOpacity
              style={[
                mobile ? cashStyles.btnMobileSecondary : styles.secondaryButton,
                mobile ? { flexGrow: 1, flexBasis: 140, minWidth: 140 } : localStyles.footerButton,
              ]}
              onPress={safeOnClose}
              disabled={vm.closingCash}
              accessibilityRole="button"
            >
              <Text style={mobile ? cashStyles.btnMobileSecondaryText : styles.secondaryButtonText}>
                Cancelar
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                mobile ? cashStyles.btnMobilePrimary : styles.primaryButton,
                mobile ? { flexGrow: 1.5, flexBasis: 160, minWidth: 160 } : localStyles.footerButton,
                !canConfirmClose && styles.disabled,
              ]}
              onPress={() => {
                void submitClose();
              }}
              disabled={!canConfirmClose}
              accessibilityRole="button"
              testID="cash.close.confirm-btn"
            >
              {vm.closingCash ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={mobile ? cashStyles.btnMobilePrimaryText : styles.primaryButtonText}>
                  {cashArqueoCopy.closeConfirmCta}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
        </KeyboardAvoidingViewIfMobile>
        {DiscardConfirmModal}
      </View>
    </Modal>
  );
}

function KeyboardAvoidingViewIfMobile({ enabled, children }: { enabled: boolean; children: React.ReactNode }) {
  if (!enabled) return <>{children}</>;
  return (
    <KeyboardAvoidingView behavior="padding" style={localStyles.keyboardWrap} automaticOffset>
      {children}
    </KeyboardAvoidingView>
  );
}

const localStyles = StyleSheet.create({
  keyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  card: {
    maxHeight: '96%',
    gap: spacing.sm,
  },
  body: {
    gap: spacing.md,
    paddingTop: spacing.xs,
  },
  heroBlock: {
    gap: 4,
    paddingBottom: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  heroTitle: {
    ...typography.titleLg,
    color: ticketColors.ink,
    fontWeight: '800',
    fontSize: 18,
    lineHeight: 24,
  },
  heroSubtitle: {
    ...typography.body,
    fontSize: 14,
    color: ticketColors.ink2,
    lineHeight: 20,
  },
  sectionTitle: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '700',
    fontSize: 11,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
  },
  breakdownBlock: {
    gap: 6,
  },
  payRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  payRowLabel: {
    ...typography.body,
    fontSize: 14,
    color: ticketColors.ink2,
    flex: 1,
    minWidth: 0,
  },
  payRowValue: {
    ...typography.body,
    fontSize: 14,
    color: ticketColors.ink,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  todayContextNote: {
    ...typography.caption,
    color: ticketColors.ink3,
    lineHeight: 16,
  },
  arqueoBlock: {
    gap: spacing.sm,
  },
  arqueoGrid: {
    flexDirection: 'row',
    gap: spacing.md,
    alignItems: 'flex-start',
  },
  arqueoColumn: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  arqueoColumnCounted: {
    flex: 1.2,
  },
  arqueoLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '700',
    fontSize: 11,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
  },
  arqueoAmount: {
    ...typography.titleLg,
    color: ticketColors.ink,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
    fontSize: 22,
    lineHeight: 28,
    paddingVertical: 10,
  },
  arqueoAmountNegative: {
    color: ticketColors.bad,
  },
  arqueoInput: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    minHeight: 48,
    color: ticketColors.ink,
    backgroundColor: ticketColors.paper2,
    ...typography.titleLg,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
    fontSize: 20,
    lineHeight: 26,
  },
  arqueoFieldHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontSize: 11,
    lineHeight: 14,
  },
  arqueoDiffExact: {
    color: ticketColors.ok,
  },
  arqueoDiffSurplus: {
    color: ticketColors.warn,
  },
  arqueoDiffWarn: {
    color: ticketColors.warn,
  },
  arqueoDiffShortage: {
    color: ticketColors.bad,
  },
  arqueoDiffPlaceholder: {
    ...typography.titleLg,
    color: ticketColors.ink3,
    fontWeight: '700',
    fontSize: 22,
    lineHeight: 28,
    paddingVertical: 10,
  },
  footerActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  footerButton: {
    flex: 1,
  },
  loadingBody: {
    paddingVertical: spacing.xl,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    gap: spacing.sm,
  },
  loadingInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  loadingText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
});
